import { useCallback, useEffect, useState } from 'react'
import { api } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Attesa, Avviso, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import {
  AreaTesto, Bottone, Etichetta, Modale, Paginazione, Riquadro, Scelta, totaleDi, type ConTotale,
} from '../componenti/base.tsx'
import { LIMITE, giorno, messaggioErrore } from '../componenti/elenco.tsx'
import { EstensioniChatRiservate } from './scheda/ChatRiservata.tsx'
import { regolaATesto, type RegolaCancellazione } from '../servizi/posta.ts'

type Approvazione = ConTotale & {
  id: string; tipo: string; oggetto_tipo: string; oggetto_id: string; motivazione: string
  stato: string; richiesta_il: string; scade_il: string; nome: string; cognome: string
  richiesta_da: string
  /** Competenza calcolata dal motore: chi decide questo tipo, oggi (sec.tipo_approvazione). */
  etichetta: string
  favorevoli: number
  necessarie: number
  ruoli_decisori: string[]
  ruoli_richiesti: string[]
  ruoli_mancanti: string[]
  permesso_decisore: string | null
  /** Ruoli che non decidono questo tipo (482). Vuoto per tutti i tipi dalla 499. */
  ruoli_esclusi: string[]
  /** Oggetto in chiaro, dove l'API riesce a risolverlo (progetto e referente di N-1). */
  oggetto_progetto: string | null
  oggetto_persona: string | null
  /** Funzione da accendere (attivazione_monitoraggio). */
  oggetto_funzione?: string | null
  /** Casella da leggere, il suo titolare e la durata chiesta (supervisione_posta). */
  oggetto_casella?: string | null
  oggetto_titolare?: string | null
  giorni_richiesti?: number | null
  /** Avviso in calce alla posta (499): il testo proposto e quello in vigore. */
  testo_proposto?: string | null
  testo_in_vigore?: string | null
  /** Regola di cancellazione della posta: quella proposta e quella in vigore (500). */
  regola_posta?: { regola: RegolaCancellazione | null; prima: RegolaCancellazione | null } | null
  /**
   * Recupero della password (520): con quale indirizzo entra la persona, se ha un'email di recupero
   * verificata (nascosta in parte), se la richiesta approvata aspetta ancora il nuovo invito.
   */
  recupero?: { email: string; stato: string; mfa_attiva: boolean; verificata: boolean; consumata: boolean; inviato_a?: string } | null
}

/** Chi decide, detto a parole invece che con il codice del permesso. */
const DECISORI_PER_PERMESSO: Record<string, string> = {
  'ruolo.assegna': 'chi assegna gli incarichi per quella sede, area o progetto',
  'approvazione.decidi': 'chi decide le approvazioni',
  'posta.supervisione_concedi': 'il Referente privacy o il DPO',
  'utente.gestisci': 'chi gestisce gli utenti, non la persona stessa',
}

/** L'oggetto della richiesta a parole, quando l'API lo risolve; altrimenti null. */
function oggettoInChiaro(a: Approvazione): string | null {
  if (a.tipo === 'modifica_avviso_in_calce') return 'Avviso in calce alle email scritte dalle persone'
  if (a.recupero) return `Utenza ${a.recupero.email}`
  if (a.oggetto_funzione) return `Funzione: ${a.oggetto_funzione}`
  if (a.regola_posta?.regola) {
    const prima = a.regola_posta.prima ? `Oggi: ${regolaATesto(a.regola_posta.prima)}. ` : ''
    return `Regola di cancellazione della posta. ${prima}Proposta: ${regolaATesto(a.regola_posta.regola)}.`
  }
  if (a.oggetto_casella) {
    const giorni = a.giorni_richiesti
      ? ` · ${a.giorni_richiesti === 1 ? 'un giorno' : `${a.giorni_richiesti} giorni`} di lettura` : ''
    return `Casella ${a.oggetto_casella}${a.oggetto_titolare ? ` di ${a.oggetto_titolare}` : ''}${giorni}`
  }
  if (a.oggetto_progetto || a.oggetto_persona) {
    return [a.oggetto_progetto, a.oggetto_persona].filter(Boolean).join(' · ')
  }
  return null
}
/**
 * Gli organi che decidono, a parole. Le regole delle approvazioni dicono «DPO» per l'organo privacy:
 * il Referente privacy o il DPO (499, servizi/avvisi.ts dell'API).
 */
const NOMI_RUOLI: Record<string, string> = {
  DIR: 'la Direzione', DPO: 'il Referente privacy o il DPO', RPR: 'il Referente privacy', RAR: 'il Project Manager',
  ADM: 'l’amministratore', COO: 'il coordinatore', OPI: 'un operatore interno', HM: 'il supporto di Honey et Molly',
}
const nomi = (ruoli: string[], e: string) => ruoli.map((r) => NOMI_RUOLI[r] ?? r).join(e)

/** Il seguito della decisione a parole (520): dove e' partito il collegamento, o che cosa fare adesso. */
function testoDelSeguito(esecuzione: Record<string, string | null> | null): string | null {
  if (!esecuzione) return null
  if (esecuzione.errore) return esecuzione.errore
  switch (esecuzione.recupero) {
    case 'collegamento_inviato':
      return `Il collegamento per la password nuova è partito verso l’email di recupero ${esecuzione.inviato_a ?? ''}.`
    case 'email_non_partita':
      return 'L’email con il collegamento non è partita'
        + `${esecuzione.motivo_email ? ` (${esecuzione.motivo_email})` : ''}: in Amministrazione → Utenti prepara un `
        + '«Nuovo invito» e consegnalo per un’altra via.'
    case 'senza_email_recupero':
      return 'Approvata. La persona non ha un’email di recupero verificata: contattala, poi in Amministrazione → Utenti '
        + 'prepara un «Nuovo invito» dalla sua riga.'
    default:
      return null
  }
}

export function Approvazioni() {
  const { io, puo } = useSessione()
  const [stato, setStato] = useState('in_attesa')
  const [elenco, setElenco] = useState<Approvazione[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [daRespingere, setDaRespingere] = useState<string | null>(null)
  /** Che cosa e' successo dopo la decisione, quando l'atto non e' solo il «sì» (520, recupero della password). */
  const [seguito, setSeguito] = useState<string | null>(null)
  const [motivo, setMotivo] = useState('')
  const [salta, setSalta] = useState(0)

  const carica = useCallback(() => {
    setElenco(null)
    api.get<Approvazione[]>(`/approvazioni?stato=${stato}&limite=${LIMITE}&salta=${salta}`)
      .then(setElenco)
      .catch((e) => setErrore(messaggioErrore(e)))
  }, [stato, salta])
  useEffect(carica, [carica])

  async function decidi(id: string, decisione: 'approvata' | 'respinta', nota: string | null) {
    setErrore(null); setSeguito(null)
    try {
      const r = await api.post<{ esecuzione: Record<string, string | null> | null }>(`/approvazioni/${id}/decidi`, { decisione, nota })
      setSeguito(testoDelSeguito(r.esecuzione))
      carica()
    } catch (e) { setErrore(messaggioErrore(e)) }
  }

  async function respingi() {
    if (!daRespingere || !motivo.trim()) return
    const id = daRespingere
    setDaRespingere(null)
    await decidi(id, 'respinta', motivo)
    setMotivo('')
  }

  /**
   * Chi decide dipende dal tipo: alcuni li decide chi ha `approvazione.decidi`, l'accesso del
   * referente al progetto lo decide chi puo' assegnare incarichi. Qui si mostra il comando a chi
   * potrebbe averne titolo; il perimetro lo verifica l'API, che risponde con il motivo esatto.
   */
  function puoDecidere(a: Approvazione): boolean {
    if (a.permesso_decisore) return puo(a.permesso_decisore)
    // Un tipo che nomina i suoi decisori (564: Direzione e amministratore di sistema per i programmi di posta) lo decide chi ne
    // porta il ruolo, anche senza approvazione.decidi: e' la regola del motore (servizi/approvazioni.ts).
    const organo = (r: string) => (r === 'RPR' ? 'DPO' : r)
    if (a.ruoli_decisori?.length) return !!io?.incarichi.some((i) => a.ruoli_decisori!.includes(organo(i.ruolo)))
    return puo('approvazione.decidi')
  }

  return (
    <>
      <Intestazione titolo="Approvazioni"
        sottotitolo="Le richieste sono decise da un utente diverso dal richiedente."
        azioni={
          <div className="filtro-intestazione">
            {/* Il cambio di stato riporta la finestra a zero: le pagine dei due stati non
                hanno alcun rapporto fra loro. */}
            <Scelta etichetta="Stato" valore={stato}
                    cambia={(v) => { setStato(v); setSalta(0) }} opzioni={[
              { valore: 'in_attesa', etichetta: 'In attesa' },
              { valore: 'approvata', etichetta: 'Approvate' },
              { valore: 'respinta', etichetta: 'Respinte' },
              { valore: 'scaduta', etichetta: 'Scadute' },
              // Annullate: richieste rimaste senza oggetto, per esempio perche' il referente
              // del progetto e' cambiato prima della decisione.
              { valore: 'annullata', etichetta: 'Annullate' },
            ]} />
          </div>
        } />

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {seguito && <Avviso tipo="informazione">{seguito}</Avviso>}
      {/* 480: le estensioni della conservazione delle chat riservate, per Direzione, DPO e Referente
          privacy (482). Hanno tabelle proprie: la decisione porta il consenso della persona, che le
          approvazioni non hanno. */}
      {stato === 'in_attesa' && <EstensioniChatRiservate />}
      {!elenco && <Attesa testo="Caricamento…" />}
      {/* La navigazione resta visibile anche sulla pagina vuota: senza, chi arriva su una
          pagina svuotata dalle decisioni prese non avrebbe modo di tornare indietro. */}
      {elenco && (
        <>
          {elenco.length === 0
            ? <Vuoto testo="Nessuna richiesta in questo stato." />
            : elenco.map((a) => (
              <Riquadro key={a.id}
                titolo={<Etichetta tono="accento">{a.etichetta ?? a.tipo.replace(/_/g, ' ')}</Etichetta>}
                azioni={<span className="piccolo tenue">Scadenza {giorno(a.scade_il)}</span>}>
                <p className="piccolo tenue">
                  {a.nome} {a.cognome} · {new Date(a.richiesta_il).toLocaleString('it-IT')}
                </p>
                <p>{a.motivazione}</p>
                {/* L'oggetto in chiaro quando l'API lo risolve: un identificativo non dice a
                    chi deve decidere che cosa sta decidendo. */}
                <p className="piccolo tenue">
                  {oggettoInChiaro(a) ?? <>{a.oggetto_tipo} <span className="mono">{a.oggetto_id}</span></>}
                </p>
                {/* Avviso in calce (499): chi decide legge il testo nuovo e quello che sostituisce. */}
                {a.tipo === 'modifica_avviso_in_calce' && (
                  <>
                    <div className="piccolo tenue">Testo proposto</div>
                    {a.testo_proposto
                      ? <p style={{ whiteSpace: 'pre-line' }}>{a.testo_proposto}</p>
                      : <p>Nessun avviso: la richiesta toglie l’avviso dalle email.</p>}
                    <details className="piccolo tenue">
                      <summary>Testo in vigore</summary>
                      <p style={{ whiteSpace: 'pre-line' }}>{a.testo_in_vigore || 'Nessun avviso.'}</p>
                    </details>
                  </>
                )}

                {/* Recupero della password (520): chi decide sa dove partira' il collegamento, o che non c'e' dove. */}
                {a.recupero && (
                  a.recupero.verificata
                    ? (
                      <p className="piccolo">
                        Approvando, parte un collegamento per scegliere la password nuova, valido un’ora, all’email di
                        recupero verificata {a.recupero.inviato_a}.
                        {a.recupero.mfa_attiva ? ' Il secondo fattore resta.' : ''}
                      </p>
                    )
                    : (
                      <Avviso tipo="attenzione">
                        Questa persona non ha un’email di recupero verificata: approvando non parte niente. Contattala per
                        un’altra via, poi in Amministrazione → Utenti prepara un «Nuovo invito» e consegnale collegamento e
                        codice.{a.stato === 'approvata' && !a.recupero.consumata ? ' Il nuovo invito non è ancora stato preparato.' : ''}
                      </Avviso>
                    )
                )}

                {/* Competenza e avanzamento: con piu' decisori la riga dice quante decisioni
                    mancano e di chi, altrimenti la richiesta sembrerebbe ferma senza motivo. */}
                <div className="referente-competenza">
                  <span>
                    Decide{' '}
                    {a.permesso_decisore
                      ? DECISORI_PER_PERMESSO[a.permesso_decisore] ?? 'chi ha l’incarico per decidere su questa richiesta'
                      : a.ruoli_decisori?.length
                        // Quando servono tutti (ruoli richiesti uguali ai decisori), «e»; altrimenti uno basta, «o».
                        ? nomi(a.ruoli_decisori, a.ruoli_richiesti?.length === a.ruoli_decisori.length && a.necessarie > 1
                          ? ' e ' : ' o ')
                        : 'chi decide le approvazioni'}
                    {/* Solo dove la frase di sopra li comprenderebbe: chi ha il permesso, non un ruolo nominato. */}
                    {!a.permesso_decisore && !a.ruoli_decisori?.length && a.ruoli_esclusi?.length
                      ? `, non ${nomi(a.ruoli_esclusi, ' né ')}` : ''}
                  </span>
                  {a.necessarie > 1 && (
                    <span className="referente-avanzamento">
                      Decisioni favorevoli: {a.favorevoli ?? 0} di {a.necessarie}
                      {a.ruoli_richiesti?.length ? ` (${nomi(a.ruoli_richiesti, ' e ')})` : ''}
                    </span>
                  )}
                </div>

                {a.ruoli_mancanti?.length > 0 && (
                  <Avviso tipo="attenzione">
                    Manca {nomi(a.ruoli_mancanti, ' e ')}: nessun incarico attivo. Finché l’organo
                    competente non è designato, questa richiesta non si può decidere.
                  </Avviso>
                )}

                {a.stato === 'in_attesa' && puoDecidere(a) && (
                  a.richiesta_da === io?.id
                    ? (
                      <Avviso tipo="attenzione">
                        Richiesta presentata dall’utente in sessione: la decisione spetta a un altro utente.
                      </Avviso>
                    )
                    : (
                      <div className="riga">
                        <Bottone variante="primario" onClick={() => void decidi(a.id, 'approvata', null)}>
                          Approva
                        </Bottone>
                        <Bottone variante="secondario"
                                 onClick={() => { setMotivo(''); setDaRespingere(a.id) }}>
                          Respingi
                        </Bottone>
                      </div>
                    )
                )}
              </Riquadro>
            ))}
          <Paginazione salta={salta} limite={LIMITE} totale={totaleDi(elenco)}
                       ricevute={elenco.length} cambia={setSalta} />
        </>
      )}

      {daRespingere && (
        <Modale titolo="Respingi la richiesta" chiudi={() => setDaRespingere(null)}
          azioni={
            <>
              <Bottone variante="discreto" onClick={() => setDaRespingere(null)}>Annulla</Bottone>
              <Bottone variante="critico" disabled={!motivo.trim()} onClick={() => void respingi()}>
                Respingi
              </Bottone>
            </>
          }>
          <AreaTesto etichetta="Motivo del rifiuto" obbligatorio righe={4}
                     valore={motivo} cambia={setMotivo} />
        </Modale>
      )}
    </>
  )
}
